import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { FormikProvider, useFormik } from 'formik';

import { BraintreeCardForm, getCardFormSchema } from '../../utils';
import { useBraintreeCard } from '../../hooks/use-braintree-card';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { InputField } from '../../../../../InputField/InputField';
import { useAppConfig } from '../../../../../../hooks/use-app-config';
import { AddressElement } from '../../../AddressElement/AddressElement';
import { CheckboxField } from '../../../../../CheckboxField/CheckboxField';
import { PayButton } from '../../../PaymentForm/components/PayButton/PayButton';
import { BraintreeInputField } from '../BraintreeInputField/BraintreeInputField';
import { EMAIL_FIELD_DEFAULT_ATTRIBUTES } from '../../../../../../constants/consts';
import { useAllowDefaultCheckSavedCardBox } from '../../../../../../hooks/use-allow-default-check-saved-card';
import { useAddressElementRef } from '../../../AddressElement/hooks/use-address-element-ref';
import { BackToSavedCardButton } from '../../../BackToSavedCardButton/BackToSavedCardButton';
import { useShowTaxCalculationFields } from '../../../../hooks/use-show-tax-calculation-fields';
import { PaymentElementContainer } from '../../../PaymentElementContainer/PaymentElementContainer';
import { PaymentMethodNotSupported } from '../../../PaymentMethodNotSupported/PaymentMethodNotSupported';

interface BraintreeCardProps {
  onReady: () => void;
  savedCardProps: {
    defaultSaveCardChecked: boolean;
    onBackButtonClicked: () => void;
    showBackButton: boolean;
  };
}

export function BraintreeCard({ onReady, savedCardProps }: BraintreeCardProps) {
  const { checkoutData } = useCheckout();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { useTaxCalculationField, showAddressField } = useShowTaxCalculationFields();
  const { AddressElementRef } = useAddressElementRef();
  const { featureFlags } = useAppConfig();
  const { isDefaultSaveCardAllowedForLocation } = useAllowDefaultCheckSavedCardBox();
  const shouldHideEmailField = featureFlags?.checkout_enforce_customer_email;
  const shouldHideFieldsLabels = featureFlags?.checkout_remove_field_labels;

  const {
    isAvailable,
    handleSubmit,
    cardNumberMetadata,
    securityCodeMetadata,
    expirationDateMetadata,
  } = useBraintreeCard(onReady);

  const showSaveMyCard = Boolean(checkoutData?.checkoutConfigurations?.showSaveMyCard);
  const formik = useFormik<BraintreeCardForm>({
    initialValues: {
      email: checkoutData?.customer?.email || '',
      cardholderName: '',
      shouldSaveCard:
        showSaveMyCard &&
        savedCardProps.defaultSaveCardChecked &&
        isDefaultSaveCardAllowedForLocation,
    },
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema: getCardFormSchema(shouldHideEmailField),
    onSubmit: () => {},
  });

  const handlePay = () => {
    formik.submitForm();
    AddressElementRef.current?.validate();
    handleSubmit(formik.values, formik.isValid, AddressElementRef.current);
  };

  if (!isAvailable) {
    return <PaymentMethodNotSupported />;
  }

  return (
    <PaymentElementContainer
      content={
        <Stack spacing={1.5}>
          {savedCardProps.showBackButton && (
            <BackToSavedCardButton onClick={savedCardProps.onBackButtonClicked} />
          )}
          <FormikProvider value={formik}>
            <InputField
              name="cardholderName"
              label={shouldHideFieldsLabels ? '' : t('cardholderName')}
              inputProps={{
                placeholder: t('cardholderName'),
              }}
            />
            {!shouldHideEmailField && (
              <InputField
                name="email"
                label={shouldHideFieldsLabels ? '' : t('email')}
                inputProps={{
                  placeholder: t('emailAddress'),
                  ...EMAIL_FIELD_DEFAULT_ATTRIBUTES,
                }}
              />
            )}
            <BraintreeInputField
              ref={cardNumberMetadata.ref}
              label={shouldHideFieldsLabels ? '' : 'Card number'}
              errorMessage={cardNumberMetadata.message}
            />
            <Stack direction="row" spacing={1.5}>
              <BraintreeInputField
                ref={expirationDateMetadata.ref}
                label={shouldHideFieldsLabels ? '' : 'Expiration date'}
                errorMessage={expirationDateMetadata.message}
              />
              <BraintreeInputField
                ref={securityCodeMetadata.ref}
                label={shouldHideFieldsLabels ? '' : 'Security code'}
                errorMessage={securityCodeMetadata.message}
              />
            </Stack>
            {useTaxCalculationField && (
              <AddressElement ref={AddressElementRef} zipOnly={!showAddressField} />
            )}

            {showSaveMyCard && (
              <Box pt={0.5}>
                <CheckboxField label={t('saveCardCheckbox')} name="shouldSaveCard" />
              </Box>
            )}
          </FormikProvider>
        </Stack>
      }
    >
      <PayButton onClick={handlePay} />
    </PaymentElementContainer>
  );
}
